import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Bar, BarChart, XAxis, YAxis, ResponsiveContainer, Cell, Legend } from "recharts";
import { CategoryCycleTime } from "@/hooks/useCycleTimeAnalytics";
import { Skeleton } from "@/components/ui/skeleton";

interface CycleTimeByCategoryProps {
  data: CategoryCycleTime[];
  isLoading: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  print_materials: 'Print Materials',
  packaging: 'Packaging',
  promotional_products: 'Promotional Products',
  signage: 'Signage',
  branded_merchandise: 'Branded Merchandise',
};

export function CycleTimeByCategory({ data, isLoading }: CycleTimeByCategoryProps) {
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Cycle Time by Category</CardTitle>
          <CardDescription>Compare average cycle times across product categories</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Cycle Time by Category</CardTitle>
          <CardDescription>Compare average cycle times across product categories</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No data available for the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map(item => ({
    category: CATEGORY_LABELS[item.category] || item.category,
    avg: item.avgDays,
    median: item.medianDays,
    jobCount: item.jobCount,
    fullLabel: `${CATEGORY_LABELS[item.category] || item.category} (${item.jobCount} jobs)`,
  }));

  const chartConfig = {
    avg: {
      label: 'Average',
      color: 'hsl(var(--primary))',
    },
    median: {
      label: 'Median',
      color: 'hsl(var(--primary) / 0.5)',
    },
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle>Cycle Time by Category</CardTitle>
        <CardDescription>Compare average cycle times across product categories</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              barGap={2}
            >
              <XAxis 
                type="number" 
                tickFormatter={(value) => `${value}d`}
                axisLine={false}
                tickLine={false}
                fontSize={12}
              />
              <YAxis 
                type="category" 
                dataKey="fullLabel" 
                width={180}
                axisLine={false}
                tickLine={false}
                fontSize={12}
                tick={{ fill: 'hsl(var(--muted-foreground))' }}
              />
              <ChartTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const data = payload[0].payload;
                  return (
                    <div className="bg-popover border rounded-lg shadow-lg p-3 space-y-1">
                      <p className="font-medium">{data.category}</p>
                      <p className="text-sm text-muted-foreground">{data.jobCount} jobs completed</p>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-sm bg-primary" />
                        <span>Average: {data.avg.toFixed(1)} days</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-sm bg-primary/50" />
                        <span>Median: {data.median.toFixed(1)} days</span>
                      </div>
                    </div>
                  );
                }}
              />
              <Bar 
                dataKey="avg" 
                fill="hsl(var(--primary))" 
                radius={[0, 4, 4, 0]}
                barSize={16}
              />
              <Bar 
                dataKey="median" 
                fill="hsl(var(--primary) / 0.5)" 
                radius={[0, 4, 4, 0]}
                barSize={16}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
        <div className="flex justify-center gap-6 mt-4">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-sm bg-primary" />
            <span className="text-muted-foreground">Average</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-sm bg-primary/50" />
            <span className="text-muted-foreground">Median</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
